import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Gift, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { redeemReward } from '../../services/loyaltyService';

interface RedeemRewardModalProps {
  onClose: () => void;
}

export default function RedeemRewardModal({ onClose }: RedeemRewardModalProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleRedeem = async () => {
    if (!code.trim()) {
      setError(t('loyalty.redeem.enterCode'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await redeemReward(code.trim().toUpperCase());

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(result.error || t('errors.generic'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-surface rounded-lg p-6 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-success" />
          </div>
          <h3 className="text-xl font-bold text-ink mb-2">
            {t('loyalty.rewards.rewardRedeemed')}
          </h3>
          <p className="text-ink-muted">
            {t('loyalty.rewards.activatedSuccess')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-hairline">
          <h3 className="text-lg font-semibold text-ink">
            {t('loyalty.redeem.title')}
          </h3>
          <button
            onClick={onClose}
            className="text-ink-subtle hover:text-ink "
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="bg-info/10 rounded-lg p-4 border border-info/30">
            <div className="flex gap-3">
              <Gift className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
              <div className="text-sm text-info">
                <p className="font-medium mb-1">{t('loyalty.redeem.howTo')}</p>
                <ol className="list-decimal list-inside space-y-1 text-info">
                  <li>{t('loyalty.redeem.step1')}</li>
                  <li>{t('loyalty.redeem.step2')}</li>
                  <li>{t('loyalty.redeem.step3')}</li>
                </ol>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              {t('loyalty.rewards.code')}
            </label>
            <Input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setError(null);
              }}
              placeholder="REWARD-ABC123"
              maxLength={20}
              className="font-mono text-center text-lg"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-hairline">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleRedeem}
            disabled={loading || !code.trim()}
            className="flex-1"
          >
            {loading ? t('common.redeeming') : t('loyalty.redeem.redeem')}
          </Button>
        </div>
      </div>
    </div>
  );
}
